import vtkRemoteView from "@kitware/vtk.js/Rendering/Misc/RemoteView";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EventsEnum } from "../../classes/enums/EventsEnum";
import { MedicalImagesType } from "../../classes/enums/MedicalImagesEnum";
import { RemoteRenderingActionEnum } from "../../classes/enums/RemoteRenderingActionEnum";
import { debounce } from "../../classes/utils/OptimizationUtils";
import RangeSlider from "../rangeSlider/RangeSlider";
import "./RemoteViewer.css";

function RemoteViewer({
  remoteRenderClient,
  viewData,
  isForStandalone = false,
  sliderWidth,
  id,
  onAddMeasurement,
  onSliceChange,
  watermarkText = null,
  secondaryWatermarkText = null,
  ...props
}) {
  const divPlaceholder = useRef(null);
  const containerRectRef = useRef(null);
  const mouseUpdateTimeoutRef = useRef(null);
  const lastUpdateTime = useRef(0);
  const needsRenderRef = useRef(false);
  const isAlreadyRenderedRef = useRef(false);
  const isProcessingRef = useRef(false);

  const [remoteView, setRemoteView] = useState(null);
  const [sliderValue, setSliderValue] = useState(viewData?.sliceData?.current);
  const [isTrackingMouse, setIsTrackingMouse] = useState(false);
  const [viewDataState] = useState(viewData);

  const THROTTLE_INTERVAL = 32;

  const normalizedViewId = useMemo(() => {
    if (!viewData?.viewId) {
      return null;
    }

    const parsed = parseInt(viewData.viewId, 10);
    if (isNaN(parsed) || parsed < 0) {
      return null;
    }

    return parsed;
  }, [viewData]);

  const scheduleRender = useCallback(() => {
    if (!needsRenderRef.current && remoteView) {
      needsRenderRef.current = true;

      if (window.requestIdleCallback && !isTrackingMouse) {
        window.requestIdleCallback(
          () => {
            if (remoteView) {
              try {
                remoteView.render();
              } catch (error) {}
            }
            needsRenderRef.current = false;
          },
          { timeout: 100 }
        );
      } else {
        requestAnimationFrame(() => {
          if (remoteView) {
            try {
              remoteView.render();
            } catch (error) {}
          }
          needsRenderRef.current = false;
        });
      }
    }
  }, [remoteView, isTrackingMouse]);

  const debouncedSliceChange = useMemo(
    () =>
      debounce(async slice => {
        try {
          if (
            !isProcessingRef.current &&
            remoteRenderClient &&
            normalizedViewId !== null
          ) {
            isProcessingRef.current = true;
            await remoteRenderClient.changeSliceLevelAsync(
              normalizedViewId,
              slice
            );
            scheduleRender();
            isProcessingRef.current = false;
          }
        } catch (error) {
          isProcessingRef.current = false;
        }
      }, 15),
    [remoteRenderClient, normalizedViewId, scheduleRender]
  );

  const changeSlice = value => {
    const newSlice = Number(value);
    setSliderValue(newSlice);
    debouncedSliceChange(newSlice);

    // Notify parent component of slice change
    if (onSliceChange) {
      onSliceChange(newSlice);
    }
  };

  const handleWheel = async e => {
    if (viewData?.sliceData) {
      changeSlice(sliderValue + e.deltaY / 100);
    }
  };

  const handleDoubleClick = useCallback(
    e => {
      if (
        !remoteRenderClient ||
        !divPlaceholder.current ||
        normalizedViewId === null
      )
        return;

      const session = remoteRenderClient.getSession();
      if (!session) return;

      let rect = containerRectRef.current;
      if (!rect) {
        rect = divPlaceholder.current.getBoundingClientRect();
        containerRectRef.current = rect;
      }
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1 - (e.clientY - rect.top) / rect.height;

      remoteRenderClient
        .safeRpcCall(RemoteRenderingActionEnum.MouseInteraction, [
          {
            action: "dblclick",
            x,
            y,
            buttonLeft: 1,
            buttonMiddle: 0,
            buttonRight: 0,
            shiftKey: e.shiftKey ? 1 : 0,
            altKey: e.altKey ? 1 : 0,
            ctrlKey: e.ctrlKey ? 1 : 0,
            metaKey: e.metaKey ? 1 : 0,
            view: normalizedViewId,
          },
        ])
        .catch(err => {});
    },
    [remoteRenderClient, normalizedViewId]
  );

  const handleMouseMove = useCallback(
    e => {
      if (!remoteRenderClient || remoteRenderClient.isReconnecting) return;
      const currentTime = performance.now();
      if (currentTime - lastUpdateTime.current < THROTTLE_INTERVAL) return;
      lastUpdateTime.current = currentTime;

      const eventData = {
        clientX: e.clientX,
        clientY: e.clientY,
        buttons: e.buttons,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
      };

      requestAnimationFrame(() => {
        if (
          !remoteRenderClient ||
          !divPlaceholder.current ||
          normalizedViewId === null
        )
          return;

        const session = remoteRenderClient.getSession();
        if (!session) {
          return;
        }

        let rect = containerRectRef.current;
        if (!rect) {
          rect = divPlaceholder.current.getBoundingClientRect();
          containerRectRef.current = rect;
        }
        const x = (eventData.clientX - rect.left) / rect.width;
        const y = 1 - (eventData.clientY - rect.top) / rect.height;

        remoteRenderClient
          .safeRpcCall(RemoteRenderingActionEnum.MouseInteraction, [
            {
              action: "move",
              x,
              y,
              buttonLeft: eventData.buttons & 1,
              buttonMiddle: eventData.buttons & 4,
              buttonRight: eventData.buttons & 2,
              shiftKey: eventData.shiftKey ? 1 : 0,
              altKey: eventData.altKey ? 1 : 0,
              ctrlKey: eventData.ctrlKey ? 1 : 0,
              metaKey: eventData.metaKey ? 1 : 0,
              view: normalizedViewId,
            },
          ])
          .catch(err => {});
        scheduleRender();
      });
    },
    [remoteRenderClient, normalizedViewId, scheduleRender]
  );

  const handleStartMouseTracking = useCallback(
    event => {
      try {
        if (!event.detail || !event.detail[0] || !event.detail[0].viewId) {
          return;
        }
        const eventViewId = parseInt(event.detail[0].viewId, 10);
        if (eventViewId === normalizedViewId) {
          setIsTrackingMouse(true);
        }
      } catch (error) {}
    },
    [normalizedViewId]
  );

  const handleEndMouseTracking = useCallback(
    event => {
      try {
        if (!event.detail || !event.detail[0] || !event.detail[0].viewId) {
          return;
        }
        const eventViewId = parseInt(event.detail[0].viewId, 10);
        if (eventViewId === normalizedViewId) {
          setIsTrackingMouse(false);
          if (mouseUpdateTimeoutRef.current) {
            clearTimeout(mouseUpdateTimeoutRef.current);
          }
        }
      } catch (error) {}
    },
    [normalizedViewId]
  );

  useEffect(() => {
    const currentTimeout = mouseUpdateTimeoutRef.current;
    window.addEventListener(
      EventsEnum.START_MOUSE_TRACKING,
      handleStartMouseTracking
    );
    window.addEventListener(
      EventsEnum.END_MOUSE_TRACKING,
      handleEndMouseTracking
    );
    return () => {
      window.removeEventListener(
        EventsEnum.START_MOUSE_TRACKING,
        handleStartMouseTracking
      );
      window.removeEventListener(
        EventsEnum.END_MOUSE_TRACKING,
        handleEndMouseTracking
      );
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, [handleStartMouseTracking, handleEndMouseTracking]);

  useEffect(() => {
    if (isTrackingMouse && divPlaceholder.current) {
      const currentElement = divPlaceholder.current;
      const currentTimeout = mouseUpdateTimeoutRef.current;
      currentElement.addEventListener("mousemove", handleMouseMove, {
        passive: true,
      });
      return () => {
        currentElement.removeEventListener("mousemove", handleMouseMove);
        if (currentTimeout) {
          clearTimeout(currentTimeout);
        }
      };
    }
  }, [isTrackingMouse, handleMouseMove]);

  useEffect(() => {
    if (
      !remoteRenderClient ||
      isAlreadyRenderedRef.current ||
      normalizedViewId === null
    )
      return;

    try {
      const rawSession = remoteRenderClient.getSession();
      if (!rawSession) {
        // Session not ready yet — leave ref false so the effect can retry
        // when deps change (e.g. after the WS connection gate drops the spinner
        // and remounts this component with a live session).
        return;
      }

      isAlreadyRenderedRef.current = true;

      const imageStream = remoteRenderClient.getImageStream();
      if (!imageStream) {
        return;
      }

      const viewStream = imageStream.createViewStream(normalizedViewId);

      if (!viewStream) {
        return;
      }

      const viewProps = {
        viewStream,
        rpcWheelEvent: RemoteRenderingActionEnum.MouseWheelInteraction,
      };

      const view = vtkRemoteView.newInstance(viewProps);
      view.setSession(rawSession);
      view.setContainer(divPlaceholder.current);
      if (isForStandalone) view.resetCamera();
      view.setInteractiveQuality(75);
      view.setInteractiveRatio(0.8);
      view.setStillQuality(100);
      view.setStillRatio(1);

      // Fire-and-forget: WSLink rejects pending calls with {code: -32099} on close.
      rawSession.call("viewport.image.animation.fps.max", [30]).catch(() => {});
      rawSession.call("viewport.image.animation.start", [normalizedViewId]).catch(() => {});

      setRemoteView(view);
    } catch (error) {
      isAlreadyRenderedRef.current = false;
    }
  }, [remoteRenderClient, normalizedViewId, viewData, isForStandalone]);

  useEffect(() => {
    const debouncedResize = debounce(async () => {
      try {
        if (remoteView && remoteView.resize && remoteRenderClient?.isConnected) {
          remoteView.resize();
        }
        if (divPlaceholder.current) {
          containerRectRef.current =
            divPlaceholder.current.getBoundingClientRect();
        }
      } catch (error) {}
    }, 100);

    const observer = new ResizeObserver(() => {
      debouncedResize();
    });

    if (divPlaceholder.current) {
      observer.observe(divPlaceholder.current);
    }

    return () => {
      observer.disconnect();
      if (remoteView) {
        try {
          remoteView.delete();
        } catch (error) {}
        setRemoteView(null);
      }
    };
  }, [remoteView]);

  useEffect(() => {
    if (remoteView) {
      scheduleRender();
    }
  }, [remoteView, scheduleRender]);

  // VTK's interactor holds a direct reference to the raw wslink session and calls
  // it on every mouse event. When the connection drops, those calls fail with
  // -32099. Detach the session on disconnect (VTK guards with `if (!session) return`)
  // and reattach it once the coordinator has given us a fresh session.
  useEffect(() => {
    if (!remoteView) return;

    const handleDisconnect = () => {
      try { remoteView.setSession(null); } catch (_) {}
    };
    const handleReconnect = () => {
      const newSession = remoteRenderClient?.getSession();
      if (newSession) {
        try { remoteView.setSession(newSession); } catch (_) {}
      }
    };

    window.addEventListener("wslink:disconnected", handleDisconnect);
    window.addEventListener("wslink:reconnected", handleReconnect);
    return () => {
      window.removeEventListener("wslink:disconnected", handleDisconnect);
      window.removeEventListener("wslink:reconnected", handleReconnect);
    };
  }, [remoteView, remoteRenderClient]);

  useEffect(() => {
    const handleUpdateViews = async event => {
      try {
        if (
          !event.detail ||
          !Array.isArray(event.detail) ||
          event.detail.length === 0
        ) {
          return;
        }

        const eventViewId = parseInt(event.detail[0], 10);
        if (eventViewId === normalizedViewId && remoteView) {
          scheduleRender();
        }
      } catch (error) {}
    };

    window.addEventListener(EventsEnum.UPDATE_VIEWS, handleUpdateViews);
    return () => {
      window.removeEventListener(EventsEnum.UPDATE_VIEWS, handleUpdateViews);
    };
  }, [normalizedViewId, remoteView, scheduleRender]);

  const handleAddMeasurement = useCallback(
    event => {
      if (!onAddMeasurement || !remoteView || !divPlaceholder.current) {
        return;
      }

      const rect = divPlaceholder.current.getBoundingClientRect();

      const normalizedX = (event.clientX - rect.left) / rect.width;
      const normalizedY = 1 - (event.clientY - rect.top) / rect.height;

      const mousePosition = [normalizedX, normalizedY];

      const clickContext = {
        clientX: event.clientX,
        clientY: event.clientY,
        containerRect: rect,
        viewId: normalizedViewId,
      };

      onAddMeasurement(mousePosition, clickContext);
    },
    [normalizedViewId, onAddMeasurement, remoteView]
  );

  const eventBasedAddMeasurement = useCallback(
    event => {
      if (
        event.type === EventsEnum.SHOW_SAGITTAL_ADVANCED_MEASUREMENT &&
        (viewDataState?.viewName === MedicalImagesType.LATERALDX ||
          viewDataState?.viewName === MedicalImagesType.LATERAL_SURGERY_DX)
      ) {
        handleAddMeasurement(event);
      } else if (
        event.type === EventsEnum.SHOW_CORONAL_ADVANCED_MEASUREMENT &&
        (viewDataState?.viewName === MedicalImagesType.FRONTDX ||
          viewDataState?.viewName === MedicalImagesType.FRONT_SURGERY_DX)
      ) {
        handleAddMeasurement(event);
      } else {
      }
    },
    [handleAddMeasurement, viewDataState]
  );

  useEffect(() => {
    window.addEventListener(
      EventsEnum.SHOW_SAGITTAL_ADVANCED_MEASUREMENT,
      eventBasedAddMeasurement
    );
    return () => {
      window.removeEventListener(
        EventsEnum.SHOW_SAGITTAL_ADVANCED_MEASUREMENT,
        eventBasedAddMeasurement
      );
    };
  }, [eventBasedAddMeasurement]);

  useEffect(() => {
    window.addEventListener(
      EventsEnum.SHOW_CORONAL_ADVANCED_MEASUREMENT,
      eventBasedAddMeasurement
    );
    return () => {
      window.removeEventListener(
        EventsEnum.SHOW_CORONAL_ADVANCED_MEASUREMENT,
        eventBasedAddMeasurement
      );
    };
  }, [eventBasedAddMeasurement]);

  return (
    <div className="App-remote-viewer-container">
      <div
        ref={divPlaceholder}
        className="App-remote-viewer"
        data-view-id={viewData?.viewId}
        onWheel={handleWheel}
        onClick={handleAddMeasurement}
        onDoubleClick={handleDoubleClick}
      />
      {watermarkText && (
        <div className="App-remote-viewer-watermark">{watermarkText}</div>
      )}
      {secondaryWatermarkText && (
        <div className="App-remote-viewer-watermark--bottom-right">{secondaryWatermarkText}</div>
      )}
      <div className="App-remote-viewer-slider-container">
        {viewData?.sliceData && normalizedViewId !== null && (
          <RangeSlider
            key={normalizedViewId}
            min={viewData.sliceData.min}
            max={viewData.sliceData.max}
            rangeValue={sliderValue}
            onRangeValueChanged={changeSlice}
          />
        )}
      </div>
    </div>
  );
}

export default RemoteViewer;
